import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PiArrowClockwiseBold, PiCheckCircleFill, PiWarningCircleFill, PiXCircleFill } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AI_BUTTON, AiDisclaimer, AiError, AiPlateIcon, AiThinking, aiErrorMessage, useAiStatus } from './aiShared';

type Analysis = {
    headline: string;
    explanation: string;
    steps: string[];
    people: { name?: string; role?: string; total?: string; note?: string }[];
    alerts: { severity?: 'alta' | 'media' | 'baixa'; text?: string }[];
    questions: string[];
    checks: { checagem: string; ok: boolean; detalhe: string }[];
    generated_at: string;
    cached: boolean;
};

const SEVERITY = {
    alta: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200',
    media: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
    baixa: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200',
};

export default function CommissionAiAnalysisButton({ dealId }: { dealId: number | string }) {
    const ai = useAiStatus();
    const [open, setOpen] = useState(false);
    const gen = useMutation({
        mutationFn: async (refresh: boolean) =>
            (await api.post<{ data: Analysis }>(`/ai/ploomes-deals/${dealId}/commission-analysis`, { refresh })).data.data,
    });

    if (!ai.data?.features.commission) return null;
    const a = gen.data;

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setOpen(true);
                    if (!gen.data && !gen.isPending) gen.mutate(false);
                }}
                title="Explicar a comissão e procurar inconsistências"
                className={cn(AI_BUTTON, 'px-2.5 py-1.5 text-xs')}
            >
                <AiPlateIcon className="size-4" />
                Analisar
            </button>
            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Análise da comissão com IA"
                description="Explicação do cálculo e checagens automáticas. Nada é alterado."
                size="2xl"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Fechar
                        </Button>
                        <Button variant="outline" disabled={gen.isPending} onClick={() => gen.mutate(true)}>
                            <PiArrowClockwiseBold className="me-1.5 size-4" /> Analisar de novo
                        </Button>
                    </>
                }
            >
                {gen.isPending ? (
                    <AiThinking label="Conferindo cálculo, parcelas e lançamentos…" />
                ) : gen.isError ? (
                    <AiError message={aiErrorMessage(gen.error)} onRetry={() => gen.mutate(false)} />
                ) : a ? (
                    <div className="space-y-4 text-sm">
                        <div>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">{a.headline}</p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{a.explanation}</p>
                        </div>

                        {a.alerts.length > 0 ? (
                            <div className="space-y-1.5">
                                {a.alerts.map((al, i) => (
                                    <p key={i} className={cn('flex gap-2 rounded-lg border px-3 py-2 text-xs', SEVERITY[al.severity ?? 'media'])}>
                                        <PiWarningCircleFill className="mt-0.5 size-4 shrink-0" />
                                        {al.text}
                                    </p>
                                ))}
                            </div>
                        ) : (
                            <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                                <PiCheckCircleFill className="size-4" /> Nenhuma inconsistência encontrada.
                            </p>
                        )}

                        {a.steps.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Como foi calculado</p>
                                <ol className="list-decimal space-y-1 ps-4 text-xs text-gray-700 dark:text-gray-200">
                                    {a.steps.map((s, i) => (
                                        <li key={i}>{s}</li>
                                    ))}
                                </ol>
                            </div>
                        )}

                        {a.people.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Quem recebe</p>
                                <div className="grid gap-1.5 sm:grid-cols-2">
                                    {a.people.map((p, i) => (
                                        <div key={i} className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-900/50">
                                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                                                {p.name} <span className="font-normal text-gray-500">· {p.role}</span>
                                            </p>
                                            <p className="font-bold tabular-nums text-gray-800 dark:text-gray-100">{p.total}</p>
                                            {p.note && <p className="text-[11px] text-gray-500">{p.note}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Checagens automáticas do Hub</p>
                            <ul className="space-y-1 text-xs">
                                {a.checks.map((c, i) => (
                                    <li key={i} className="flex gap-1.5">
                                        {c.ok ? <PiCheckCircleFill className="mt-0.5 size-3.5 shrink-0 text-emerald-500" /> : <PiXCircleFill className="mt-0.5 size-3.5 shrink-0 text-rose-500" />}
                                        <span className="text-gray-700 dark:text-gray-200">
                                            {c.checagem} <span className="text-gray-400">— {c.detalhe}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {a.questions.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Para confirmar</p>
                                <ul className="list-disc space-y-1 ps-4 text-xs text-gray-700 dark:text-gray-200">
                                    {a.questions.map((q, i) => (
                                        <li key={i}>{q}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <AiDisclaimer generatedAt={a.generated_at} cached={a.cached} />
                    </div>
                ) : null}
            </V2Modal>
        </>
    );
}
